import React, { useState, useEffect } from 'react';
import { Box, Typography, Select, MenuItem, FormControl, InputLabel, Grid, Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip } from '@mui/material';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import axios from 'axios';
import moment from 'moment';

// Função para gerar cores automaticamente
const generateColor = (index) => {

    
    const colors = [
        '#FF5733', '#33FF57', '#3357FF', '#FF33A1', '#A133FF', '#33FFF2',
        '#F2FF33', '#FFB733', '#FF3357', '#33A1FF', '#A1FF33', '#FF3385',
        '#3385FF', '#85FF33', '#FF5733', '#9933FF', '#33FFB8', '#FFB833',
        '#B833FF', '#338FFF', '#FF3366', '#FF9933', '#33FF66', '#3366FF',
        '#FF3380', '#33FFD2', '#FF6F33', '#33FFAF', '#3366CC', '#CC33FF',
        '#66FF33', '#FFCC33', '#336633', '#FF3333', '#3399FF', '#FF6633',
        '#66FF99', '#33FF33', '#9966FF', '#FF33CC', '#FF6633', '#33FFFF',
        '#99FF33', '#FF99CC', '#99CCFF', '#FFCC99', '#CCFF33', '#CC33FF',
        '#33CCFF', '#FF33FF', '#33FF99', '#FF3399', '#FF66CC', '#66CCFF',
        '#CCFF66', '#99FF99', '#66FFCC', '#FF9933'
    ];
    return colors[index % colors.length];
};


const FeriasManagement = () => {
    const [empregados, setEmpregados] = useState([]);
    const [ferias, setFerias] = useState([]);
    const [filteredFerias, setFilteredFerias] = useState([]);
    const [seccao, setSeccao] = useState('');
    const [turno, setTurno] = useState('');
    const [selectedEmpregadoId, setSelectedEmpregadoId] = useState(null); // Novo estado para armazenar o empregado selecionado
    const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
    const [currentTab, setCurrentTab] = useState(0); // Estado para controlar a tab atual
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Ano selecionado para visualização
    const apiBaseURL = 'https://plasticemployers.duckdns.org';      // IP da rede local

    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('access');
            try {
                // Fetch employees with holidays data calculated in backend
                const responseEmpregados = await axios.get(`${apiBaseURL}/api/empregados-ferias/?ano=${selectedYear}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const employeesWithColor = responseEmpregados.data.map((employee, index) => ({
                    ...employee,
                    color: generateColor(index),
                }));
                setEmpregados(employeesWithColor);
        
                // Still fetch ferias for the calendar view
                const responseFerias = await axios.get(`${apiBaseURL}/api/ferias/`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                console.log('Férias carregadas:', responseFerias.data);
                responseFerias.data.forEach((feria) => {
                    if (!feria.empregado) {
                        console.log('Aviso: ferias sem empregado', feria);
                    }
                });
                setFerias(responseFerias.data);
                setFilteredFerias(responseFerias.data);
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }
        };
        
        fetchData();
    }, [selectedYear]);
    
    useEffect(() => {
        const filtered = ferias.filter((feria) => {
            const employee = empregados.find((e) => e.id === feria.empregado);
            if (!employee) {
                console.log(`Empregado com id ${feria.empregado} não encontrado.`);
                return false; // Se o empregado não for encontrado, ignorar esse registo
            }
            // Verifica se a secção e turno correspondem, caso contrário, retorna true para mostrar todos
            return (
                (seccao ? employee?.seccao === seccao : true) &&
                (turno ? employee?.turno === turno : true)
            );
        });
        
        console.log('Férias filtradas: ', filtered); // Verificar as férias filtradas
        setFilteredFerias(filtered);
    }, [seccao, turno, ferias, empregados]);
    
    const parseFeriaDates = (diasFerias) => {
        if (!diasFerias) return [];
        return diasFerias
            .split(',')
            .map((day) => day.trim())
            .filter(Boolean)
            .filter((day) => moment(day, 'YYYY-MM-DD', true).isValid());
    };

    const empregadosById = React.useMemo(() => {
        const map = {};
        empregados.forEach((empregado) => {
            map[empregado.id] = empregado;
        });
        return map;
    }, [empregados]);

    const calendarEntriesByDate = React.useMemo(() => {
        const entriesByDate = {};

        filteredFerias.forEach((feria) => {
            const employee = empregadosById[feria.empregado];
            if (!employee) {
                return;
            }

            const isAprovado = Boolean(feria.data_aprovacao || feria.aprovado_por);
            const dates = parseFeriaDates(feria.dias_ferias);

            dates.forEach((dateStr) => {
                if (!entriesByDate[dateStr]) {
                    entriesByDate[dateStr] = [];
                }

                entriesByDate[dateStr].push({
                    id: `${feria.id}-${dateStr}`,
                    feriaId: feria.id,
                    date: dateStr,
                    approved: isAprovado,
                    halfDay: Boolean(feria.half_day),
                    employeeId: employee.id,
                    employeeName: employee.nome_completo,
                    seccao: employee.seccao || '-',
                    turno: employee.turno || '-',
                    color: employee.color,
                });
            });
        });

        Object.values(entriesByDate).forEach((entries) => {
            entries.sort((a, b) => {
                if (a.approved !== b.approved) return a.approved ? -1 : 1;
                return a.employeeName.localeCompare(b.employeeName, 'pt');
            });
        });

        return entriesByDate;
    }, [filteredFerias, empregadosById]);

    const selectedCalendarDateKey = moment(selectedCalendarDate).format('YYYY-MM-DD');
    const selectedCalendarEntries = calendarEntriesByDate[selectedCalendarDateKey] || [];

    const selectedCalendarStats = React.useMemo(() => {
        const approved = selectedCalendarEntries.filter((entry) => entry.approved).length;
        const pending = selectedCalendarEntries.length - approved;
        const halfDays = selectedCalendarEntries.filter((entry) => entry.halfDay).length;
        return {
            total: selectedCalendarEntries.length,
            approved,
            pending,
            halfDays,
        };
    }, [selectedCalendarEntries]);

    const getTileClassName = ({ date, view }) => {
        if (view !== 'month') return null;
        const key = moment(date).format('YYYY-MM-DD');
        const count = (calendarEntriesByDate[key] || []).length;
        if (count >= 6) return 'ferias-tile ferias-tile--dense';
        if (count >= 3) return 'ferias-tile ferias-tile--busy';
        if (count > 0) return 'ferias-tile ferias-tile--has-events';
        return null;
    };

    const getTileContent = ({ date, view }) => {
        if (view === 'month') {
            const dateKey = moment(date).format('YYYY-MM-DD');
            const entries = calendarEntriesByDate[dateKey] || [];

            if (entries.length === 0) {
                return null;
            }

            const approvedCount = entries.filter((entry) => entry.approved).length;
            const pendingCount = entries.length - approvedCount;
            const hiddenCount = Math.max(0, entries.length - 3);

            return (
                <Box
                    sx={{
                        mt: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.25,
                        px: 0.25,
                    }}
                >
                    <Box
                        sx={{
                            minWidth: 22,
                            px: 0.75,
                            borderRadius: 999,
                            bgcolor: entries.length >= 6 ? 'error.main' : entries.length >= 3 ? 'warning.main' : 'primary.main',
                            color: 'common.white',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            lineHeight: 1.4,
                            textAlign: 'center',
                        }}
                    >
                        {entries.length}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, minHeight: 10 }}>
                        {entries.slice(0, 3).map((entry) => (
                            <Box
                                key={entry.id}
                                title={`${entry.employeeName}${entry.halfDay ? ' (meio dia)' : ''}${entry.approved ? ' - Aprovado' : ' - Pendente'}`}
                                sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: entry.color,
                                    opacity: entry.approved ? 1 : 0.45,
                                    border: entry.approved ? 'none' : '1px solid rgba(0,0,0,0.35)',
                                }}
                            />
                        ))}
                        {hiddenCount > 0 && (
                            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary', lineHeight: 1 }}>
                                +{hiddenCount}
                            </Typography>
                        )}
                    </Box>
                    <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary', lineHeight: 1 }}>
                        {approvedCount}A / {pendingCount}P
                    </Typography>
                </Box>
            );
        }
        return null;
    };
    

    const handleEmployeeHighlight = (empregadoId) => {
        setSelectedEmpregadoId(empregadoId); // Atualiza o estado com o ID do empregado selecionado
    };

    const handleSeccaoChange = (event) => {
        setSeccao(event.target.value);
    };

    const handleTurnoChange = (event) => {
        setTurno(event.target.value);
    };

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    const handleYearChange = (event) => {
        setSelectedYear(event.target.value);
        // Trigger data refetch when year changes
        const fetchData = async () => {
            const token = localStorage.getItem('access');
            try {
                const responseEmpregados = await axios.get(`${apiBaseURL}/api/empregados-ferias/?ano=${event.target.value}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const employeesWithColor = responseEmpregados.data.map((employee, index) => ({
                    ...employee,
                    color: generateColor(index),
                }));
                setEmpregados(employeesWithColor);
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }
        };
        fetchData();
    };

    // Get holidays data from backend (already calculated)
    const getHolidaysData = (empregado) => {
        // If ferias_data exists (from backend), use it
        if (empregado.ferias_data) {
            return empregado.ferias_data;
        }
        // Fallback if data not available
        return {
            base: empregado.remaining_holidays || 0,
            standard: 22,
            halfDays: 0,
            consumed: 0,
            marked: 0,
            totalUsed: 0,
            remaining: (empregado.remaining_holidays || 0) + 22,
            ano: selectedYear
        };
    };

    // Get filtered employees based on seccao and turno filters
    const getFilteredEmployees = () => {
        return empregados.filter((empregado) => {
            return (
                (seccao ? empregado.seccao === seccao : true) &&
                (turno ? empregado.turno === turno : true)
            );
        });
    };

    // Generate available years (current year and next few years)
    const availableYears = [];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear - 1; i <= currentYear + 2; i++) {
        availableYears.push(i);
    }

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>Gestão de Férias</Typography>

            <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
                <Tab label="Calendário" />
                <Tab label="Dias de Férias Restantes" />
            </Tabs>

            {currentTab === 0 && (
	                <>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Seção</InputLabel>
                                <Select value={seccao} onChange={handleSeccaoChange}>
                                    <MenuItem value="">Todas as Seções</MenuItem>
                                    <MenuItem value="Admin">Admin</MenuItem>
                                    <MenuItem value="Escritorio">Escritório</MenuItem>
                                    <MenuItem value="Design">Design</MenuItem>
                                    <MenuItem value="Corte">Corte</MenuItem>
                                    <MenuItem value="Impressao">Impressão</MenuItem>
                                    <MenuItem value="Mini-Flexo">Mini-Flexo</MenuItem>
                                    <MenuItem value="Digital">Digital</MenuItem>
                                    <MenuItem value="Digital-rotativa">Digital-rotativa</MenuItem>
                                    <MenuItem value="Armazem">Armazém</MenuItem>
                                    <MenuItem value="Logistica">Logística</MenuItem>
                                    <MenuItem value="Comercial">Comercial</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Turno</InputLabel>
                                <Select value={turno} onChange={handleTurnoChange}>
                                    <MenuItem value="">Todos os Turnos</MenuItem>
                                    <MenuItem value="manha-1">Manhã-1</MenuItem>
                                    <MenuItem value="manha">Manhã</MenuItem>
                                    <MenuItem value="tarde">Tarde</MenuItem>
                                    <MenuItem value="noite">Noite</MenuItem>
                                    <MenuItem value="normal">Normal</MenuItem>
                                    <MenuItem value="horario_amamentacao">Horário de Amamentação</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

	                    <Box
	                        sx={{
	                            display: 'flex',
	                            flexDirection: { xs: 'column', lg: 'row' },
	                            gap: 3,
	                            mt: 4,
	                            '& .react-calendar': {
	                                width: '100%',
	                                border: 'none',
	                                borderRadius: 2,
	                                padding: 1.5,
	                                backgroundColor: '#fafafa',
	                            },
	                            '& .react-calendar__navigation button': {
	                                borderRadius: 1,
	                                fontWeight: 700,
	                            },
	                            '& .react-calendar__tile': {
	                                minHeight: 86,
	                                borderRadius: 1.5,
	                                paddingTop: '8px',
	                                paddingBottom: '8px',
	                                display: 'flex',
	                                flexDirection: 'column',
	                                alignItems: 'center',
	                                justifyContent: 'flex-start',
	                            },
	                            '& .react-calendar__tile:enabled:hover': {
	                                backgroundColor: '#eef6ff',
	                            },
	                            '& .react-calendar__tile--active': {
	                                backgroundColor: '#dbeafe',
	                                color: '#0f172a',
	                            },
	                            '& .react-calendar__tile--now': {
	                                backgroundColor: '#fff7cc',
	                            },
	                            '& .ferias-tile--has-events': {
	                                boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.18)',
	                            },
	                            '& .ferias-tile--busy': {
	                                boxShadow: 'inset 0 0 0 2px rgba(237, 108, 2, 0.25)',
	                                background: 'linear-gradient(180deg, rgba(255,243,224,0.8) 0%, rgba(255,255,255,1) 60%)',
	                            },
	                            '& .ferias-tile--dense': {
	                                boxShadow: 'inset 0 0 0 2px rgba(211, 47, 47, 0.25)',
	                                background: 'linear-gradient(180deg, rgba(255,235,238,0.9) 0%, rgba(255,255,255,1) 60%)',
	                            },
	                        }}
	                    >
	                        <Box sx={{ flex: 1, minWidth: 0 }}>
	                            <Paper sx={{ p: 2 }}>
	                                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
	                                    Calendário de Férias
	                                </Typography>
	                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
	                                    Cada dia mostra total de pessoas, mini-indicadores e separação Aprovado/Pendente.
	                                </Typography>
	                                <Calendar
	                                    value={selectedCalendarDate}
	                                    onClickDay={setSelectedCalendarDate}
	                                    tileContent={getTileContent}
	                                    tileClassName={getTileClassName}
	                                />
	                            </Paper>
	                        </Box>
	                        <Box sx={{ width: { xs: '100%', lg: 360 }, flexShrink: 0 }}>
	                            <Paper sx={{ p: 2, mb: 2 }}>
	                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
	                                    {moment(selectedCalendarDate).format('DD/MM/YYYY')}
	                                </Typography>
	                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
	                                    Detalhe das férias para o dia selecionado
	                                </Typography>
	                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
	                                    <Chip label={`Total: ${selectedCalendarStats.total}`} size="small" />
	                                    <Chip label={`Aprovados: ${selectedCalendarStats.approved}`} size="small" color="success" variant="outlined" />
	                                    <Chip label={`Pendentes: ${selectedCalendarStats.pending}`} size="small" color="warning" variant="outlined" />
	                                    <Chip label={`Meios dias: ${selectedCalendarStats.halfDays}`} size="small" color="secondary" variant="outlined" />
	                                </Box>
	                                {selectedCalendarEntries.length === 0 ? (
	                                    <Typography variant="body2" color="text.secondary">
	                                        Sem férias registadas neste dia.
	                                    </Typography>
	                                ) : (
	                                    <Box sx={{ display: 'grid', gap: 1, maxHeight: 380, overflowY: 'auto', pr: 0.5 }}>
	                                        {selectedCalendarEntries.map((entry) => (
	                                            <Box
	                                                key={entry.id}
	                                                onClick={() => handleEmployeeHighlight(entry.employeeId)}
	                                                sx={{
	                                                    p: 1.25,
	                                                    borderRadius: 1.5,
	                                                    border: '1px solid',
	                                                    borderColor: 'divider',
	                                                    cursor: 'pointer',
	                                                    backgroundColor: selectedEmpregadoId === entry.employeeId ? 'action.selected' : 'background.paper',
	                                                    '&:hover': { backgroundColor: 'action.hover' },
	                                                }}
	                                            >
	                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
	                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
	                                                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color, flexShrink: 0 }} />
	                                                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
	                                                            {entry.employeeName}
	                                                        </Typography>
	                                                    </Box>
	                                                    <Chip
	                                                        size="small"
	                                                        label={entry.approved ? 'Aprovado' : 'Pendente'}
	                                                        color={entry.approved ? 'success' : 'warning'}
	                                                        variant={entry.approved ? 'filled' : 'outlined'}
	                                                    />
	                                                </Box>
	                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
	                                                    {entry.seccao} • {entry.turno}
	                                                    {entry.halfDay ? ' • Meio dia' : ''}
	                                                </Typography>
	                                            </Box>
	                                        ))}
	                                    </Box>
	                                )}
	                            </Paper>
	                            <Paper sx={{ p: 2 }}>
	                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
	                                    Legenda (empregados filtrados)
	                                </Typography>
	                                <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
	                                    {empregados
	                                        .filter(empregado => {
	                                            return (
	                                                (seccao ? empregado.seccao === seccao : true) &&
	                                                (turno ? empregado.turno === turno : true)
	                                            );
	                                        })
	                                        .map(empregado => (
	                                            <Box 
	                                                key={empregado.id} 
	                                                sx={{ 
	                                                    display: 'flex', 
	                                                    alignItems: 'center', 
	                                                    mb: 1.25,
	                                                    p: 0.5,
	                                                    borderRadius: 1,
	                                                    backgroundColor: empregado.id === selectedEmpregadoId ? 'action.selected' : 'transparent'
	                                                }}
	                                            >
	                                                <Box
	                                                    sx={{
	                                                        width: 14,
	                                                        height: 14,
	                                                        backgroundColor: empregado.color,
	                                                        borderRadius: '50%',
	                                                        marginRight: 1,
	                                                        flexShrink: 0,
	                                                    }}
	                                                />
	                                                <Typography variant="body2" noWrap title={empregado.nome_completo}>
	                                                    {empregado.nome_completo}
	                                                </Typography>
	                                            </Box>
	                                        ))
	                                    }
	                                </Box>
	                            </Paper>
	                        </Box>
	                    </Box>
	                </>
	            )}

            {currentTab === 1 && (
                <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Ano</InputLabel>
                                <Select value={selectedYear} onChange={handleYearChange}>
                                    {availableYears.map(year => (
                                        <MenuItem key={year} value={year}>{year}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Seção</InputLabel>
                                <Select value={seccao} onChange={handleSeccaoChange}>
                                    <MenuItem value="">Todas as Seções</MenuItem>
                                    <MenuItem value="Admin">Admin</MenuItem>
                                    <MenuItem value="Escritorio">Escritório</MenuItem>
                                    <MenuItem value="Design">Design</MenuItem>
                                    <MenuItem value="Corte">Corte</MenuItem>
                                    <MenuItem value="Impressao">Impressão</MenuItem>
                                    <MenuItem value="Mini-Flexo">Mini-Flexo</MenuItem>
                                    <MenuItem value="Digital">Digital</MenuItem>
                                    <MenuItem value="Digital-rotativa">Digital-rotativa</MenuItem>
                                    <MenuItem value="Armazem">Armazém</MenuItem>
                                    <MenuItem value="Logistica">Logística</MenuItem>
                                    <MenuItem value="Comercial">Comercial</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth margin="normal">
                                <InputLabel>Turno</InputLabel>
                                <Select value={turno} onChange={handleTurnoChange}>
                                    <MenuItem value="">Todos os Turnos</MenuItem>
                                    <MenuItem value="manha-1">Manhã-1</MenuItem>
                                    <MenuItem value="manha">Manhã</MenuItem>
                                    <MenuItem value="tarde">Tarde</MenuItem>
                                    <MenuItem value="noite">Noite</MenuItem>
                                    <MenuItem value="normal">Normal</MenuItem>
                                    <MenuItem value="horario_amamentacao">Horário de Amamentação</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Nome</strong></TableCell>
                                    <TableCell><strong>Seção</strong></TableCell>
                                    <TableCell><strong>Turno</strong></TableCell>
                                    <TableCell align="right"><strong>Saldo Base</strong></TableCell>
                                    <TableCell align="right"><strong>Dias Padrão Anuais</strong></TableCell>
                                    <TableCell align="right"><strong>Meios Dias</strong></TableCell>
                                    <TableCell align="right"><strong>Dias Gozados</strong></TableCell>
                                    <TableCell align="right"><strong>Dias Marcados</strong></TableCell>
                                    <TableCell align="right"><strong>Dias Restantes</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getFilteredEmployees()
                                    .map(empregado => {
                                        const holidaysData = getHolidaysData(empregado);
                                        return (
                                            <TableRow key={empregado.id}>
                                                <TableCell>{empregado.nome_completo}</TableCell>
                                                <TableCell>{empregado.seccao || '-'}</TableCell>
                                                <TableCell>{empregado.turno || '-'}</TableCell>
                                                <TableCell align="right">{holidaysData.base}</TableCell>
                                                <TableCell align="right">{holidaysData.standard}</TableCell>
                                                <TableCell align="right">{holidaysData.halfDays}</TableCell>
                                                <TableCell align="right">{holidaysData.consumed.toFixed(1)}</TableCell>
                                                <TableCell align="right">{holidaysData.marked.toFixed(1)}</TableCell>
                                                <TableCell align="right">
                                                    <Typography 
                                                        variant="body1" 
                                                        sx={{ 
                                                            fontWeight: 'bold',
                                                            color: holidaysData.remaining < 0 ? 'error.main' : 'primary.main'
                                                        }}
                                                    >
                                                        {holidaysData.remaining.toFixed(1)}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
};

export default FeriasManagement;
